import i18n from 'i18next';
import type { InitOptions, TFunction } from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  CATALOG_NAMESPACES,
  SUPPORTED_LOCALES,
  getCatalogText,
  normalizeLocale,
  type SupportedLocale,
} from './i18n/catalog-registry';
import { getI18nDebugContext } from './i18n/debug-context';
import { recordMissingTranslationKey } from './i18n/missing-key-registry';
import { browserLogger } from './runtime/logging/browser-logger';

function detectInitialLocale(): SupportedLocale {
  const storage = typeof window !== 'undefined' && typeof window.localStorage?.getItem === 'function'
    ? window.localStorage
    : null;
  const savedLocale = storage?.getItem('i18nextLng') ?? null;
  const normalizedSaved = normalizeLocale(savedLocale);
  if (normalizedSaved) return normalizedSaved;

  if (typeof navigator !== 'undefined') {
    for (const candidate of navigator.languages ?? [navigator.language]) {
      const normalized = normalizeLocale(candidate);
      if (normalized) return normalized;
    }
  }
  return 'en';
}

function syncDocumentLanguage(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = SUPPORTED_LOCALES[locale].languageTag;
}

const initialLocale = detectInitialLocale();
const showMissingKeyMarkers = import.meta.env.DEV || import.meta.env.MODE === 'test';
const productionMissingKeyFallback =
  getCatalogText(SUPPORTED_LOCALES.en.catalog, 'common.i18n.contentUnavailable') ?? '—';

/** Pure missing-key display policy used by runtime and regression tests. */
export function missingKeyDisplay(
  namespace: string,
  key: string,
  showMarker = showMissingKeyMarkers,
): string {
  return showMarker ? `⟦${namespace}:${key}⟧` : productionMissingKeyFallback;
}

function reportMissingKey(locale: string, namespace: string, key: string): void {
  const debugContext = getI18nDebugContext();
  const route = typeof window !== 'undefined'
    ? window.location.pathname
    : debugContext?.route;
  const entry = {
    locale,
    namespace,
    key,
    route,
    descriptorId: debugContext?.descriptorId,
  };

  if (import.meta.env.MODE === 'test') {
    recordMissingTranslationKey(entry);
  }

  browserLogger.warnOnce(
    `${locale}:${namespace}:${key}:${route ?? ''}`,
    'i18n.missing_key',
    '[i18n] missing key',
    entry,
  );
}

function initializationOptions(): InitOptions {
  return {
    lng: initialLocale,
    fallbackLng: 'en',
    supportedLngs: Object.keys(SUPPORTED_LOCALES),
    defaultNS: 'common',
    ns: CATALOG_NAMESPACES,
    resources: {
      en: SUPPORTED_LOCALES.en.catalog,
      ne: SUPPORTED_LOCALES.ne.catalog,
    },
    cleanCode: true,
    load: 'languageOnly',
    saveMissing: true,
    parseMissingKeyHandler: (key: string, _defaultValue?: string, options?: { ns?: string | string[] }) => {
      const rawNamespace = options?.ns;
      const namespace = Array.isArray(rawNamespace)
        ? (rawNamespace[0] ?? '')
        : (rawNamespace ?? '');
      return missingKeyDisplay(namespace, key);
    },
    missingKeyHandler: (languages, namespace, key) => {
      reportMissingKey(languages[0] ?? initialLocale, namespace, key);
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  } as InitOptions;
}

let initializationPromise: Promise<TFunction> | null = null;

/**
 * The single application i18n initialization boundary. All entry points await
 * this promise before rendering UI, so raw keys cannot flash during bootstrap.
 */
export function initializeI18n(): Promise<TFunction> {
  if (i18n.isInitialized) return Promise.resolve(i18n.t);
  if (!initializationPromise) {
    initializationPromise = i18n
      .use(initReactI18next)
      .init(initializationOptions())
      .then((t) => {
        syncDocumentLanguage(normalizeLocale(i18n.language) ?? initialLocale);
        return t;
      });
  }
  return initializationPromise;
}

export const i18nReady = initializeI18n();

i18n.on('languageChanged', (language) => {
  const normalized = normalizeLocale(language) ?? 'en';
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined' && localStorage) {
    try {
      localStorage.setItem('i18nextLng', normalized);
    } catch {
      // localStorage may be unavailable (private browsing, test environment)
    }
  }
  syncDocumentLanguage(normalized);
  browserLogger.debug('i18n.language_changed', 'Application language changed', {
    locale: normalized,
    languageTag: SUPPORTED_LOCALES[normalized].languageTag,
  });
});

export default i18n;
